import { ChangeDetectionStrategy, Component, Input, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent, DialogConfig } from '@vsolv/vectors-ui/dialog';
import { Asset } from '@wsphere/assets/domain';
import { Policy } from '@wsphere/warranties/domain';
import { CurrencyMaskConfig } from '@vsolv/vectors-ui/input';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { PolicyService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-edit-cancelation-rule-dialog',
  templateUrl: './edit-cancelation-rule.dialog.html',
  standalone: false,
})
export class EditCancelationRuleDialog {
  constructor(
    private formBuilder: FormBuilder,
    private policySvc: PolicyService,
    private toastSvc: ToastService
  ) {}

  @ViewChild(DialogComponent) dialog!: DialogComponent;
  @Input() dialogConfig?: DialogConfig;
  @Input() policy?: Policy.Model;

  saving = false;

  currencyMaskConfig: CurrencyMaskConfig = {
    align: 'left',
    allowNegative: false,
    allowZero: false,
    decimal: '.',
    nullable: false,
    precision: 2,
    prefix: '$',
    suffix: '',
    thousands: ',',
    inputMode: 0,
  };

  cancelationRule!: Policy.CancelationRule | null;
  COUNTRIES = Asset.COUNTRIES;

  form = this.formBuilder.group({
    country: ['US', Validators.required],
    state: ['', Validators.required],
    adminFee: [0],
    deductClaims: [true, Validators.required],
    ruleText: [''],
  });

  resetFormValue = {
    country: 'US',
    state: '',
    adminFee: 0,
    deductClaims: true,
    ruleText: '',
  };

  @Input() takenStates: string[] | null = null;

  @Input() set policyCancelationRule(cancelationRule: Policy.CancelationRule | null) {
    this.cancelationRule = cancelationRule;
    if (cancelationRule) {
      this.resetFormValue = {
        country: cancelationRule.country || '',
        state: cancelationRule.state || '',
        adminFee: cancelationRule.adminFee ?? 0,
        deductClaims: cancelationRule.deductClaims ?? true,
        ruleText: cancelationRule.ruleText || '',
      };
    } else {
      this.resetFormValue = {
        country: 'US',
        state: '',
        adminFee: 0,
        deductClaims: true,
        ruleText: '',
      };
    }
    this.resetForm();
  }

  stateValues$ = new BehaviorSubject<{ name: string; abbreviation: string }[]>(Asset.US_STATES);
  states$ = this.form.controls.country.valueChanges.pipe(
    distinctUntilChanged(),
    tap(country => this.validateForm(country)),
    tap(country => {
      if (!this.validateState(this.form.value.state ?? null, country)) {
        this.form.controls.state.reset(null);
      }
    })
  );

  async resetForm() {
    try {
      this.form.reset(this.resetFormValue);
      this.form.markAsPristine();
    } catch (e) {
      console.error(e);
    }
  }

  open() {
    this.dialog.open();
  }

  close() {
    this.dialog.close();
  }

  cancel() {
    this.form.reset(this.resetFormValue);
    this.form.markAsPristine();
    this.close();
  }

  async submit() {
    this.saving = true;
    if (!this.policy) return;

    if (this.cancelationRule) {
      const dto: Policy.UpdatePolicyCancelationRuleRequest = {
        country: this.form.value.country ?? this.resetFormValue.country,
        state: this.form.value.state ?? this.resetFormValue.state,
        adminFee: this.form.value.adminFee ?? undefined,
        deductClaims: this.form.value.deductClaims ?? this.resetFormValue.deductClaims,
        ruleText: this.form.value.ruleText ?? undefined,
      };
      const updatedRule = await this.policySvc.updateCancelationRule(this.policy.id, dto).catch(({ error }) => {
        this.toastSvc.show({
          type: 'error',
          title: 'Something went wrong',
          text: error.message,
        });
        this.close();
        this.saving = false;
      });
      const updatedRuleState =
        updatedRule && typeof updatedRule === 'object' && 'state' in updatedRule ? updatedRule.state : undefined;
      if (updatedRuleState) {
        this.policyCancelationRule = null;
        this.toastSvc.show({
          type: 'success',
          title: 'Updated cancellation rule',
          text: 'The rule for <strong>' + updatedRuleState + '</strong> has been successfully updated.',
        });
        this.close();
        this.policySvc.refreshPolicy();
        this.saving = false;
      }
    } else {
      const dto: Policy.CreatePolicyCancelationRuleRequest = {
        country: this.form.value.country ?? this.resetFormValue.country,
        state: this.form.value.state ?? this.resetFormValue.state,
        adminFee: this.form.value.adminFee ?? undefined,
        deductClaims: this.form.value.deductClaims ?? this.resetFormValue.deductClaims,
        ruleText: this.form.value.ruleText ?? undefined,
      };
      const createdRule = await this.policySvc.createCancelationRule(this.policy.id, dto).catch(({ error }) => {
        this.toastSvc.show({
          type: 'error',
          title: 'Something went wrong',
          text: error.message,
        });
        this.close();
        this.saving = false;
      });
      const createdRuleState =
        createdRule && typeof createdRule === 'object' && 'state' in createdRule ? createdRule.state : undefined;
      if (createdRuleState) {
        this.policyCancelationRule = null;
        this.toastSvc.show({
          type: 'success',
          title: 'Created cancellation rule',
          text: 'The rule for <strong>' + createdRuleState + '</strong> has been successfully created.',
        });
        this.close();
        this.policySvc.refreshPolicy();
        this.saving = false;
      }
    }
  }

  validateForm(country: string | null) {
    if (country === 'CA') {
      this.stateValues$.next([...Asset.CA_PROVINCES]);
    } else if (country === 'US') {
      this.stateValues$.next(Asset.US_STATES);
    } else {
      this.stateValues$.next([...Asset.CA_PROVINCES, ...Asset.US_STATES]);
    }
    this.form.updateValueAndValidity();
  }

  validateState(state: string | null, country: string | null) {
    if (
      state &&
      ((country === 'US' && !Asset.US_STATES.some(item => item.abbreviation === state)) ||
        (country === 'CA' && !Asset.CA_PROVINCES.some(item => item.abbreviation === state)) ||
        (country === null &&
          (!Asset.US_STATES.some(item => item.abbreviation === state) ||
            !Asset.CA_PROVINCES.some(item => item.abbreviation === state))))
    ) {
      return null;
    }
    return state;
  }
}
